import axios from 'axios';
import { MapData_Base } from '~/common/typescript/models/MapData';
import { remote_mapDataFile_getInfo } from '~/common/typescript/apis/MapDataFileAPIs';
import { API_URL_ADMIN } from '~/config/config';
import { showMapDataLayerPopup } from '../../controllers/popups/MapDataLayerPopup';
import { MapDataPopupCandidate } from '../../controllers/store/MapDataLayerAtom';
import { ParsedMapDataMetadata } from './MapDataMetadataParser';
import CadStyleFactory from '~/services/cad/CadStyleFactory';

export const MAP_DATA_SOURCE_ID_PREFIX = 'MAP_DATA_SOURCE_ID_PREFIX_';
export const MAP_DATA_LAYER_ID_PREFIX = 'MAP_DATA_LAYER_ID_PREFIX_';

export const mapDataEvents: Record<string, { onMouseMove: (e: any) => void; onMouseLeave?: () => void; onClick: (e: any) => void }> = {};
const activeMapDataRegistry: Record<number, { mapData: MapData_Base; layerIds: string[] }> = {};
let lastGlobalMapDataClickTime = 0;

/**
 * Check whether a vector dataset originates from CAD (MicroStation DGN, AutoCAD DWG/DXF)
 */
export function isCadDataset(
  mapData: MapData_Base,
  parsed: ParsedMapDataMetadata,
  vectorLayers?: any[]
): boolean {
  // 1. Check map data name extension
  const name = (mapData.name || '').toLowerCase();
  if (name.endsWith('.dgn') || name.endsWith('.dwg') || name.endsWith('.dxf')) {
    return true;
  }

  // 2. Check attached file extensions in map_data_files
  if (Array.isArray(mapData.map_data_files)) {
    const hasCadFile = mapData.map_data_files.some((f: any) => {
      const fn = (f.name || f.path || '').toLowerCase();
      return fn.endsWith('.dgn') || fn.endsWith('.dwg') || fn.endsWith('.dxf');
    });
    if (hasCadFile) return true;
  }

  // 3. Check metadata flags & driver
  const meta: any = parsed.metadata || {};
  if (meta.isCad === true || meta.isDgn === true || meta.isDwg === true) {
    return true;
  }
  const driver = (meta.driverShortName || meta.driver_short_name || '').toUpperCase();
  if (['DGN', 'DGNV8', 'DWG', 'DXF', 'AUTOCAD'].includes(driver)) {
    return true;
  }

  // 4. Check category slug/name
  const cat = typeof mapData.category === 'string'
    ? mapData.category.toLowerCase()
    : (((mapData.category as any)?.name || (mapData.category as any)?.slug || '') as string).toLowerCase();
  if (cat.includes('cad') || cat.includes('dgn') || cat.includes('dwg')) {
    return true;
  }

  // 5. Check vector layer names (e.g. Level_1, Level_2, Level 10, cad)
  if (Array.isArray(vectorLayers) && vectorLayers.length > 0) {
    const levelCount = vectorLayers.filter((vl: any) => {
      const id = String(vl.id || vl.layer || '');
      return /^level[_\s]?\d+/i.test(id) || /^cad/i.test(id);
    }).length;
    if (levelCount > 0 && (levelCount >= 2 || levelCount >= vectorLayers.length * 0.3)) {
      return true;
    }
  }

  return false;
}

export const MapDataVectorLayer = {
  addToMap: async (mapData: MapData_Base, parsed: ParsedMapDataMetadata, theFirstAboveLayerId?: string) => {
    const map = window.map;
    if (!map || !mapData.id) return;

    const mapDataId = mapData.id;
    const sourceId = MAP_DATA_SOURCE_ID_PREFIX + mapDataId;

    // Remove any existing layers using this source before replacing source
    const existingLayers = map.getStyle()?.layers || [];
    existingLayers.forEach((layer: any) => {
      if (layer.source === sourceId || layer.id.includes(`${MAP_DATA_LAYER_ID_PREFIX}${mapDataId}`)) {
        try {
          map.removeLayer(layer.id);
        } catch (_) {}
      }
    });

    if (map.getSource(sourceId)) {
      try {
        map.removeSource(sourceId);
      } catch (_) {}
    }

    const { tileFile, styleFile } = parsed;
    if (!tileFile) {
      console.warn('MapDataVectorLayer - No tileFile (pmtiles/mbtiles) found for vector layer:', mapDataId);
      return;
    }

    // Lấy thông tin MBTiles/PMTiles để có minZoom/maxZoom và vector_layers
    const tileInfo = await remote_mapDataFile_getInfo(tileFile.path);
    const minzoom = tileInfo?.minzoom;
    const maxzoom = tileInfo?.maxzoom;
    console.log('MapDataVectorLayer - Vector layer minzoom/maxzoom:', { minzoom, maxzoom });

    const url = `${API_URL_ADMIN}/api/map-data-file-ext/vector/{z}/{x}/{y}.pbf?path=${tileFile.path}`;

    map.addSource(sourceId, {
      type: 'vector',
      tiles: [url],
      minzoom: minzoom,
      maxzoom: maxzoom,
    });

    let style: any;
    if (styleFile) {
      try {
        const styleResult = await axios.get(
          `${API_URL_ADMIN}/api/map-data-file-ext/download?filename=${styleFile.name}&mapDataFileId=${styleFile.id}`,
        );
        style = styleResult?.data;
      } catch (err) {
        console.error('Failed to load custom style file:', err);
      }
    }

    const layerIds: string[] = [];
    const vectorLayers = tileInfo?.vector_layers || [];

    if (style && Array.isArray(style.layers)) {
      MapDataVectorLayer.addStyledLayers(map, mapDataId, sourceId, style.layers, layerIds, theFirstAboveLayerId);
    } else if (isCadDataset(mapData, parsed, vectorLayers)) {
      MapDataVectorLayer.addCadLayers(map, mapData, sourceId, vectorLayers, layerIds, parsed, theFirstAboveLayerId);
    } else if ((parsed.metadata as any)?.styleConfig) {
      MapDataVectorLayer.addCustomGisLayers(map, mapData, sourceId, vectorLayers, layerIds, parsed, theFirstAboveLayerId);
    } else {
      MapDataVectorLayer.addDefaultLayers(map, mapDataId, sourceId, vectorLayers, layerIds, theFirstAboveLayerId);
    }

    MapDataVectorLayer.setupInteractions(map, mapData, layerIds);
  },

  addCadLayers: (
    map: any,
    mapData: MapData_Base,
    sourceId: string,
    vectorLayers: any[],
    layerIds: string[],
    parsed: ParsedMapDataMetadata,
    theFirstAboveLayerId?: string,
  ) => {
    let layers: string[] = [];
    if (Array.isArray(vectorLayers) && vectorLayers.length > 0) {
      layers = vectorLayers.map((vl: any) => String(vl.id || vl.layer)).filter(Boolean);
    }
    if (layers.length === 0 && Array.isArray((parsed.metadata as any)?.layers)) {
      layers = (parsed.metadata as any).layers;
    }
    if (layers.length === 0) {
      layers = ['default'];
    }

    const layerColors = (parsed.metadata as any)?.layerColors || {};
    const layerPriorities = (parsed.metadata as any)?.layerPriorities || {};
    const heightDivisor = (parsed.metadata as any)?.heightDivisor || 1;
    const prefix = `${MAP_DATA_LAYER_ID_PREFIX}${mapData.id}-`;

    const cadStyles = CadStyleFactory.createDefaultCadStyles(
      String(mapData.id),
      layers,
      layerColors,
      heightDivisor,
      layerPriorities,
      prefix,
    );

    console.log(`[MapDataVectorLayer] Applying CAD styles for "${mapData.name}" (${mapData.id}):`, {
      levelsCount: layers.length,
      subLayersCount: cadStyles.layers.length,
    });

    cadStyles.layers.forEach((subLayer) => {
      const subLayerId = subLayer.id;

      try {
        const payload: any = {
          id: subLayerId,
          type: subLayer.type,
          source: sourceId,
          'source-layer': subLayer['source-layer'],
          paint: subLayer.paint,
        };
        if (subLayer.filter) {
          payload.filter = subLayer.filter;
        }
        if (subLayer.layout) {
          payload.layout = subLayer.layout;
        }
        if (typeof subLayer.minzoom === 'number') {
          payload.minzoom = subLayer.minzoom;
        }
        if (typeof subLayer.maxzoom === 'number') {
          payload.maxzoom = subLayer.maxzoom;
        }

        map.addLayer(payload, theFirstAboveLayerId);
        layerIds.push(subLayerId);
      } catch (err) {
        console.error('Failed to add CAD sublayer:', subLayerId, err);
      }
    });
  },

  addCustomGisLayers: (
    map: any,
    mapData: MapData_Base,
    sourceId: string,
    vectorLayers: any[],
    layerIds: string[],
    parsed: ParsedMapDataMetadata,
    theFirstAboveLayerId?: string,
  ) => {
    let layers: string[] = [];
    if (Array.isArray(vectorLayers) && vectorLayers.length > 0) {
      layers = vectorLayers.map((vl: any) => String(vl.id || vl.layer)).filter(Boolean);
    }
    if (layers.length === 0) layers = ['default'];

    const prefix = `${MAP_DATA_LAYER_ID_PREFIX}${mapData.id}-`;
    const styleConfig = (parsed.metadata as any).styleConfig;
    const gisStyles = CadStyleFactory.createCustomGisStyles(
      String(mapData.id),
      layers,
      styleConfig,
      prefix,
    );

    console.log(`[MapDataVectorLayer] Applying custom GIS styles for "${mapData.name}" (${mapData.id}):`, {
      subLayersCount: gisStyles.layers.length,
      symbologyMode: styleConfig?.symbologyMode,
    });

    gisStyles.layers.forEach((subLayer) => {
      const subLayerId = subLayer.id;
      try {
        const payload: any = {
          id: subLayerId,
          type: subLayer.type,
          source: sourceId,
          'source-layer': subLayer['source-layer'],
          paint: subLayer.paint,
        };
        if (subLayer.filter) payload.filter = subLayer.filter;
        if (subLayer.layout) payload.layout = subLayer.layout;
        if (typeof subLayer.minzoom === 'number') payload.minzoom = subLayer.minzoom;
        if (typeof subLayer.maxzoom === 'number') payload.maxzoom = subLayer.maxzoom;

        map.addLayer(payload, theFirstAboveLayerId);
        layerIds.push(subLayerId);
      } catch (err) {
        console.error('Failed to add custom GIS sublayer:', subLayerId, err);
      }
    });
  },

  addStyledLayers: (
    map: any,
    mapDataId: number,
    sourceId: string,
    layers: any[],
    layerIds: string[],
    theFirstAboveLayerId?: string,
  ) => {
    layers.forEach((layer: any, index: number) => {
      const type = layer.type;
      const subLayerId = `${MAP_DATA_LAYER_ID_PREFIX}${mapDataId}-${index}-${type}`;

      try {
        const payload: any = {
          id: subLayerId,
          type: type,
          source: sourceId,
          'source-layer': layer['source-layer'],
          paint: layer.paint,
        };
        if (layer.filter) {
          payload.filter = layer.filter;
        }
        if (type === 'symbol' && layer.layout) {
          payload.layout = layer.layout;
        }

        map.addLayer(payload, theFirstAboveLayerId);
        layerIds.push(subLayerId);
      } catch (err) {
        console.error('Failed to add styled layer:', subLayerId, err);
      }
    });
  },

  addDefaultLayers: (
    map: any,
    mapDataId: number,
    sourceId: string,
    vectorLayers: any[],
    layerIds: string[],
    theFirstAboveLayerId?: string,
  ) => {
    const defaultColor = '#3b82f6';
    const subLayerTypes = ['fill', 'line', 'circle', 'symbol'];

    vectorLayers.forEach((vl: any) => {
      subLayerTypes.forEach((subType) => {
        const subLayerId = `${MAP_DATA_LAYER_ID_PREFIX}${mapDataId}-${vl.id}-${subType}`;

        const layerDef: any = {
          id: subLayerId,
          source: sourceId,
          'source-layer': vl.id,
          type: subType,
        };

        if (subType === 'fill') {
          layerDef.paint = {
            'fill-color': [
              'coalesce',
              ['get', 'fill'],
              ['get', 'fill_color'],
              ['get', 'color_hex'],
              ['get', 'color'],
              defaultColor,
            ],
            'fill-opacity': [
              'to-number',
              ['coalesce', ['get', 'fill-opacity'], ['get', 'fillOpacity'], 0.2],
              0.2,
            ],
          };
          layerDef.filter = ['==', '$type', 'Polygon'];
        } else if (subType === 'line') {
          layerDef.paint = {
            'line-color': [
              'coalesce',
              ['get', 'stroke'],
              ['get', 'stroke_color'],
              ['get', 'color_hex'],
              ['get', 'color'],
              defaultColor,
            ],
            'line-width': [
              'to-number',
              ['coalesce', ['get', 'stroke-width'], ['get', 'strokeWidth'], ['get', 'width'], 1.5],
              1.5,
            ],
            'line-opacity': [
              'to-number',
              ['coalesce', ['get', 'stroke-opacity'], ['get', 'line-opacity'], 1.0],
              1.0,
            ],
          };
          layerDef.filter = ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']];
        } else if (subType === 'circle') {
          layerDef.paint = {
            'circle-color': [
              'coalesce',
              ['get', 'marker-color'],
              ['get', 'color_hex'],
              ['get', 'color'],
              defaultColor,
            ],
            'circle-radius': [
              'to-number',
              ['coalesce', ['get', 'marker-size'], ['get', 'radius'], 5],
              5,
            ],
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
          };
          layerDef.filter = ['==', '$type', 'Point'];
        } else if (subType === 'symbol') {
          layerDef.minzoom = 12;
          layerDef.filter = [
            'any',
            ['has', 'name'],
            ['has', 'Name'],
            ['has', 'label'],
            ['has', 'Label'],
            ['has', 'text'],
            ['has', 'Text'],
          ];
          layerDef.paint = {
            'text-color': ['coalesce', ['get', 'text-color'], ['get', 'color_hex'], '#0f172a'],
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
          };
          layerDef.layout = {
            'text-field': [
              'coalesce',
              ['get', 'name'],
              ['get', 'Name'],
              ['get', 'label'],
              ['get', 'Label'],
              ['get', 'text'],
              ['get', 'Text'],
              '',
            ],
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
            'text-size': 12,
            'text-anchor': 'top',
            'text-offset': [0, 0.6],
            'text-optional': true,
          };
        }

        try {
          map.addLayer(layerDef, theFirstAboveLayerId);
          layerIds.push(subLayerId);
        } catch (err) {
          console.error('Failed to add default layer:', subLayerId, err);
        }
      });
    });
  },

  setupInteractions: (map: any, mapData: MapData_Base, layerIds: string[]) => {
    if (mapData.id) {
      activeMapDataRegistry[mapData.id] = { mapData, layerIds };
    }

    layerIds.forEach((layerId) => {
      const onMouseMove = () => {
        map.getCanvas().style.cursor = 'pointer';
      };

      const onMouseLeave = () => {
        map.getCanvas().style.cursor = '';
      };

      const onClick = async (e: any) => {
        const now = Date.now();
        if (now - lastGlobalMapDataClickTime < 80) return;
        lastGlobalMapDataClickTime = now;

        // Collect all currently active MapData vector layer IDs on the map
        const allLayerIds = (map.getStyle()?.layers || [])
          .map((l: any) => l.id)
          .filter((id: string) => id && id.startsWith(MAP_DATA_LAYER_ID_PREFIX));

        let rawFeatures: any[] = [];
        try {
          if (allLayerIds.length > 0 && e.point) {
            const exact = map.queryRenderedFeatures(e.point, { layers: allLayerIds }) || [];
            const bufferRadius = 12;
            const buffered =
              map.queryRenderedFeatures(
                [
                  [e.point.x - bufferRadius, e.point.y - bufferRadius],
                  [e.point.x + bufferRadius, e.point.y + bufferRadius],
                ],
                { layers: allLayerIds },
              ) || [];
            rawFeatures = [...exact, ...buffered, ...(e.features || [])];
          } else {
            rawFeatures = e.features || [];
          }
        } catch {
          rawFeatures = e.features || [];
        }

        if (!rawFeatures.length) {
          return;
        }

        const candidates: MapDataPopupCandidate[] = [];
        const seenKeys = new Set<string>();

        for (const feat of rawFeatures) {
          if (!feat) continue;
          const featLayerId: string = feat.layer?.id || '';
          const match = featLayerId.match(new RegExp(`^${MAP_DATA_LAYER_ID_PREFIX}(\\d+)`));
          const resolvedMapDataId = match ? Number(match[1]) : mapData.id;
          const targetMapData =
            (resolvedMapDataId && activeMapDataRegistry[resolvedMapDataId]?.mapData) || mapData;

          const sourceLayer =
            feat.sourceLayer || feat['source-layer'] || feat.layer?.['source-layer'] || '';
          const featureId =
            feat.properties?.gcid ??
            feat.properties?.ogc_fid ??
            feat.id ??
            feat.properties?.id ??
            feat.properties?.ID ??
            feat.properties?.OBJECTID ??
            feat.properties?.fid;

          const dedupKey = `${targetMapData.id ?? ''}::${sourceLayer}::${
            featureId !== undefined && featureId !== null
              ? String(featureId)
              : JSON.stringify(feat.properties || {})
          }`;

          if (seenKeys.has(dedupKey)) continue;
          seenKeys.add(dedupKey);

          const title = sourceLayer
            ? `${targetMapData.name} (${sourceLayer})`
            : targetMapData.name || 'Đối tượng bản đồ';

          candidates.push({
            title,
            properties: feat.properties || {},
            geometry: feat.geometry,
            mapDataId: targetMapData.id,
            mapDataName: targetMapData.name,
            sourceLayer,
            featureId,
          });
        }

        if (candidates.length === 0) return;

        const first = candidates[0];
        showMapDataLayerPopup({
          ...first,
          lngLat: [e.lngLat.lng, e.lngLat.lat],
          items: candidates,
          selectedIndex: 0,
        });
      };

      map.on('mousemove', layerId, onMouseMove);
      map.on('mouseleave', layerId, onMouseLeave);
      map.on('click', layerId, onClick);

      mapDataEvents[layerId] = {
        onMouseMove,
        onMouseLeave,
        onClick,
      };
    });
  },

  cleanupEvents: (mapDataId: number) => {
    delete activeMapDataRegistry[mapDataId];
    const map = window.map;
    if (!map) return;

    Object.keys(mapDataEvents).forEach((layerId) => {
      if (layerId.includes(`${MAP_DATA_LAYER_ID_PREFIX}${mapDataId}`)) {
        const events = mapDataEvents[layerId];
        if (events) {
          map.off('mousemove', layerId, events.onMouseMove);
          if (events.onMouseLeave) {
            map.off('mouseleave', layerId, events.onMouseLeave);
          }
          map.off('click', layerId, events.onClick);
          delete mapDataEvents[layerId];
        }
      }
    });
  },
};
